import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Document, DocumentReader, FieldDataType, View } from 'src/app/modules/core/store/models';
import { DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { DistanceService } from 'src/app/modules/core/services/distance/distance.service';
import { UtilService } from 'src/app/modules/cop/services/util.service';
//import { Store } from '@ngrx/store';
import numeral from 'numeral';
import moment from 'moment';
import { DynamicField } from 'src/app/modules/core/components/fields/dynamic-field';
import { RelatedDocumentCard } from '../related-ducument-model';
import { TranslateService } from '@ngx-translate/core';
import { CultureService } from '../../../services/culture.service';
import { MAX_CARD_VIEW_CONTROLS } from '@app/core/constants';

@Component({
    selector: 'topo-related-document-card',
    templateUrl: './related-document-card.component.html',
    styleUrls: ['./related-document-card.component.scss'],
    standalone: false
})
export class RelatedDocumentCardComponent implements OnInit {
  @Input() card: RelatedDocumentCard;
  @Input() isActive: boolean;
  @Input() allowRemove: boolean;
  @Output() click: EventEmitter<RelatedDocumentCard> = new EventEmitter();
  @Output() zoomToDocument: EventEmitter<RelatedDocumentCard> = new EventEmitter();

  imageUrl: any;
  address: string;
  location: any;
  summary: string;
  DatasetType = DatasetType;
  DatasetSubtype = DatasetSubtype;
  icon: string;
  iconBgColor: string;
  iconFgColor: string;
  typeName: string;
  postedDateTime: any;
  postedElapsedTime: any;
  categoryName: string;
  documentControls: DynamicField[] = [];
  isFacilityAsset: boolean;
  isPersonAsset: boolean;
  isFacilityOrPersonAsset: boolean;
  assetName: string;
  defaultImageUrl = 'https://files.topo.ai/images/office-image.svg';
  customFieldsSet: boolean;
  truncatedView: View;
  typeNameTranslated: string;
  personName: string;
  encodedImage: string;

  constructor(private distanceService: DistanceService, private utilService: UtilService, private cultureService: CultureService) {}

  ngOnInit(): void {
    const reader = new DocumentReader(this.card.document);

    const provider = reader.provider?.toLowerCase() || 'notFound';

    this.isFacilityAsset = this.card?.dataset?.type === DatasetType.Assets && this.card?.dataset?.subtype === DatasetSubtype.Facility;
    this.isPersonAsset =
      this.card?.dataset?.type === DatasetType.Assets &&
      (this.card?.dataset?.subtype === DatasetSubtype.Person || this.card?.dataset?.subtype === DatasetSubtype.HorizonPerson);
    this.isFacilityOrPersonAsset =
      this.card?.dataset?.type === DatasetType.Assets &&
      (this.card?.dataset?.subtype === DatasetSubtype.Facility ||
        this.card?.dataset?.subtype === DatasetSubtype.Person ||
        this.card?.dataset?.subtype === DatasetSubtype.HorizonPerson);

    this.customFieldsSet = this.card?.view?.controls.length > 0;

    if (this.card?.view?.controls?.length > MAX_CARD_VIEW_CONTROLS) {
      this.truncatedView = {
        ...this.card?.view,
        controls: this.card?.view?.controls.slice(0, MAX_CARD_VIEW_CONTROLS)
      };
    } else {
      this.truncatedView = this.card?.view;
    }

    if (this.card?.dataset?.type === DatasetType.Alerts) {
      this.summary =
        reader.emailSubject || reader.summary || reader.title || reader.name || reader.content || reader.getFieldValue('display_name');
      if (this.summary?.length > 150) {
        this.summary = this.summary.slice(0, 150);
      }
    } else {
      this.summary =
        reader.name || reader.emailSubject || reader.summary || reader.title || reader.content || reader.getFieldValue('display_name');
      if (this.summary?.length > 50) {
        this.summary = this.summary.slice(0, 50);
      }
    }

    this.encodedImage = reader.getFieldValue('snapshot');

    if (this.card?.dataset?.subtype === DatasetSubtype.Person || this.card?.dataset?.subtype === DatasetSubtype.HorizonPerson) {
      this.address = reader.address ?? reader.getFieldValue('address_1') ?? reader.getFieldValue('address_2');
      // Standard field names are "first_name" and "last_name" but we have also used "first" and "last" previously
      const first = reader.getFieldValue('first_name') ?? reader.getFieldValue('first');
      const last = reader.getFieldValue('last_name') ?? reader.getFieldValue('last');
      if (first && last) {
        this.assetName = first + ' ' + last;
      } else if (first) {
        this.assetName = first;
      } else if (last) {
        this.assetName = last;
      } else {
        this.assetName = this.summary;
      }

      this.personName = this.assetName?.replace(/^(mr|mrs|miss|ms|dr)[\.\s]*\b/gi, '');
    } else if (this.card?.dataset?.subtype === DatasetSubtype.TravelItinerary) {
      const first = reader.getFieldValue('first_name');
      const last = reader.getFieldValue('last_name');
      if (first && last) {
        this.assetName = first + ' ' + last;
      } else if (first) {
        this.assetName = first;
      } else if (last) {
        this.assetName = last;
      } else {
        this.assetName = this.summary;
      }

      if (!this.summary && this.assetName) {
        this.summary = this.assetName;
      }
    } else {
      this.address = reader.address;
      this.assetName = this.summary;
    }

    [this.icon, this.iconBgColor, this.iconFgColor] = [null, null, null];

    this.categoryName = reader?.getFieldValue('category');

    if (this.card?.dataset?.type === DatasetType.Incident) {
      this.typeName = reader.getFieldValue('number');
    }

    if (!!this.card?.dataset?.schema) {
      if (!!reader.getFieldValue('image')) {
        const imageField = this.card?.dataset?.schema.fields.find(f => f.dataType === FieldDataType.Image);
        if (!!imageField) {
          this.imageUrl = reader.getFieldValue(imageField.systemName);
        }
      } else {
        this.imageUrl = reader.getFieldValue('image');
      }

      let layerGeoField = this.card?.layer?.displayOptions?.geoFieldsToDisplay?.length > 0 ? this.card?.layer?.displayOptions?.geoFieldsToDisplay[0] : null;

      const geoField =
        this.card?.dataset?.schema.fields.find(f => f.dataType === FieldDataType.Geometry && f.systemName === layerGeoField) ??
        this.card?.dataset?.schema.fields.find(f => f.dataType === FieldDataType.Geometry && f.settings?.defaultGeometry === true) ??
        this.card?.dataset?.schema.fields.find(f => f.dataType === FieldDataType.Geometry);

      if (!!geoField /* && !!this.card.layerUId*/) {
        const geoValue = reader.getFieldValue(geoField.systemName);
        if (!!geoValue) {
          this.location = this.getLatLngForDisplay(geoValue);
        } else {
          this.location = null;
        }
      } else {
        this.location = null;
      }
    }

    let posted = reader.posted;
    if (!posted) {
      const val = reader.getFieldValue('published_date');
      posted = !!val ? moment(val) : null;
    }
    if (!posted) {
      const val = reader.getFieldValue('email_date');
      posted = !!val ? moment(val) : null;
    }
    if (!posted) {
      posted = reader.dateCreated;
    }
    if (!!posted) {
      this.postedDateTime = this.cultureService.getDateDisplay(posted, 'LONG-DOW-MONTH-DAY-YEAR-WITH-TIME');
      this.postedElapsedTime = posted.from(new Date());
    }
  }

  clickCard() {
    this.click.emit(this.card);
  }

  getTypeName(type: DatasetType) {
    this.typeNameTranslated = `MANAGEMENT.DATASETS.DATASET-TYPE-${type}`;
    this.typeName = null;
  }

  onZoomToDocument() {
    this.zoomToDocument.emit(this.card);
  }

  getLatLngForDisplay(geometry: any) {
    if (!geometry) {
      return null;
    }
    const data = JSON.parse(geometry);
    const normalizedData = this.utilService.getNormalizedGeometriesOrFeaturesFromGeoJson(data);
    const bounds = this.distanceService.getBoundsForGeometryFeatures(normalizedData);
    if (!bounds) {
      return null;
    }

    const center = bounds.getCenter();
    const lat = numeral(center.lat());
    const lng = numeral(center.lng());

    return `${lat.format('0[.]0000')}, ${lng.format('0[.]0000')}`;
  }
}
